<script setup lang="ts">
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { Button } from '~/components/ui/button'
import { ContentBlock, type TContentBlockVariant } from '~/components/ui/content-block'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '~/components/ui/select'
import { WidgetPhoneInput } from '~/components/widget/phone-input'
import { useApiPost } from '~/composables/useApiPost'
import { CurrencyIcon } from '~/components/ui/currency-icon'
import type { FormServerErrorResponse, ILocation, IResponseData } from '~/types'
import type { IDeposit } from '~/components/entity/deposit/item'
import type { IOrganization } from '~/components/entity/organization/types'
import { WidgetInnInput } from '~/components/widget/inn-input'
import { FeatureCustomConsent } from '~/components/feature/custom-consent'
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert'
import { AlertCircle } from 'lucide-vue-next'
import { useToast } from '~/components/ui/toast'
import { ANCHOR_IDS } from '~/lib/constants'
import type { THeading } from '~/components/ui/heading'

const { t } = useI18n()
const config = useRuntimeConfig()
const { toast } = useToast()

// Pre-compute all validation messages to avoid calling useValidationMessage in computed
const validationMessages = {
  requiredFieldLastName: useValidationMessage('requiredFieldLastName'),
  minValue2: useValidationMessage('minValue', 2),
  maxValue50: useValidationMessage('maxValue', 50),
  requiredFieldFirstName: useValidationMessage('requiredFieldFirstName'),
  requiredEmailInvalid: useValidationMessage('requiredEmailInvalid'),
  requiredCity: useValidationMessage('requiredCity'),
  chooseCurrency: useValidationMessage('chooseCurrency'),
  requiredDepositAmount: useValidationMessage('requiredDepositAmount'),
  chooseDepositLength: useValidationMessage('chooseDepositLength'),
  requiredInn: useValidationMessage('requiredInn'),
  agreement: useValidationMessage('agreement'),

}

const apiParams = {
  per_page: 100
}

interface IProps {
  availableCurrencies?: { id: number; code: string }[]
  deposit?: IDeposit
  organization?: IOrganization
  variant?: TContentBlockVariant
  headingText?: string
  headingTag?: THeading
  disableAnchor?: boolean
}

const props = defineProps<IProps>()

// Organization may arrive either directly or through the selected product
const leadOrganization = computed(() => props.organization ?? props.deposit?.organization)

const isInnRequired = computed(() => leadOrganization.value?.permissions?.is_inn_required === true)

// Bank-specific consent replaces our own agreement when the detail response provides it
const customConsent = computed(() => props.deposit?.custom_consent ?? props.organization?.custom_consent ?? null)

const { data } = useApi<IResponseData<ILocation[]>>(
  () => useUrlWithParams('/locations', apiParams),
  {
    key: 'locations'
  }
)
const cities = computed(() => data.value?.data)

const defaultPeriods = [
  { value: '3', title: t('form.credit_request.periods.months_3') },
  { value: '6', title: t('form.credit_request.periods.months_6') },
  { value: '12', title: t('common.periods.year_1') },
  { value: '18', title: t('form.credit_request.periods.months_18') },
  { value: '24', title: t('common.periods.years_2') },
  { value: '36', title: t('form.credit_request.periods.months_36') },
  { value: '48', title: t('form.credit_request.periods.months_48') },
  { value: '60', title: t('common.periods.years_5') }
]

const periods = computed(() => {
  if (!props.deposit?.rates || !form.values.currency_id) {
    return defaultPeriods
  }

  // Get available periods for selected currency
  const selectedCurrencyId = form.values.currency_id
  const availablePeriodsFromRates = props.deposit.rates
    .filter((rate) => rate.currency_id === selectedCurrencyId)
    .map((rate) => rate.period)
    .filter((period, index, self) => self.indexOf(period) === index) // unique values
    .sort((a, b) => a - b) // sort ascending

  if (availablePeriodsFromRates.length === 0) {
    return defaultPeriods
  }

  // Convert periods to display format
  return availablePeriodsFromRates.map((period) => {
    const periodStr = period.toString()
    let title = ''

    if (period < 12) {
      // Months
      if (period === 1) title = t('common.periods.month_1')
      else if (period >= 2 && period <= 4) title = t('common.periods.months_2_4', { n: period })
      else title = t('common.periods.months_5_', { n: period })
    } else if (period % 12 === 0) {
      // Years
      const years = period / 12
      if (years === 1) title = t('common.periods.year_1')
      else if (years >= 2 && years <= 4) title = t('common.periods.years_2_4', { n: years })
      else if (years >= 5 && years <= 20) title = t('common.periods.years_5', { n: years })
      else {
        const lastDigit = years % 10
        const lastTwoDigits = years % 100

        if (lastTwoDigits >= 11 && lastTwoDigits <= 14)
          title = t('common.periods.years_5', { n: years })
        else if (lastDigit === 1) title = t('common.periods.year_1_', { n: years })
        else if (lastDigit >= 2 && lastDigit <= 4)
          title = t('common.periods.years_2_4', { n: years })
        else title = t('common.periods.years_5', { n: years })
      }
    } else {
      // Mixed periods (not full years)
      title = t('common.periods.months_5_', { n: period })
    }

    return { value: periodStr, title }
  })
})

const depositCurrencies = [
  {
    id: 1,
    code: 'KGS'
  },
  {
    id: 2,
    code: 'USD'
  },
  {
    id: 3,
    code: 'EUR'
  },
  {
    id: 4,
    code: 'RUB'
  }
]

const currencies = computed(() => {
  return props.availableCurrencies ? props.availableCurrencies : depositCurrencies
})

// Built in setup: the phone schema resolves its messages through useI18n
const phoneSchema = usePhoneValidationSchema()

const formSchema = computed(() =>
  toTypedSchema(
    z.object({
      last_name: z
        .string({ message: validationMessages.requiredFieldLastName })
        .min(2, { message: validationMessages.minValue2 })
        .max(50, { message: validationMessages.maxValue50 }),
      first_name: z
        .string({ message: validationMessages.requiredFieldFirstName })
        .min(2, { message: validationMessages.minValue2 })
        .max(50, { message: validationMessages.maxValue50 }),
      middle_name: z.string().optional(),
      inn: useInnValidationSchema(isInnRequired.value, validationMessages.requiredInn),
      phone: phoneSchema,
      email: z
        .string()
        .email({ message: validationMessages.requiredEmailInvalid })
        .optional()
        .or(z.literal('')),
      location_id: z.number().min(0, { message: validationMessages.requiredCity }),
      currency_id: z.number().min(1, { message: validationMessages.chooseCurrency }),
      amount: z.preprocess(
        (val) => (val === '' ? undefined : Number(val)),
        z
          .number({
            invalid_type_error: validationMessages.requiredDepositAmount,
            required_error: validationMessages.requiredDepositAmount
          })
          .min(1, { message: validationMessages.requiredDepositAmount })
      ),
      period: z.string().min(1, { message: validationMessages.chooseDepositLength }),
      agreement: z
        .boolean({
          required_error: validationMessages.agreement
        })
        .refine((val) => val, {
          message: validationMessages.agreement
        })
    })
  )
)

const form = useForm({
  validationSchema: formSchema,
  initialValues: {
    location_id: cities.value?.[0]?.id ?? undefined,
    currency_id: currencies.value[0]?.id ?? undefined,
    period: defaultPeriods[0].value,
    agreement: false
  }
})

watch(cities, (newCities) => {
  if (newCities?.length && !form.values.location_id) {
    form.setFieldValue('location_id', newCities[0].id)
  }
})

// Watch currency changes to update available periods
watch(
  () => form.values.currency_id,
  (newCurrencyId) => {
    if (newCurrencyId && props.deposit?.rates) {
      const availablePeriods = periods.value
      if (availablePeriods.length > 0) {
        // Check if current period is still available
        const currentPeriod = form.values.period
        const isCurrentPeriodAvailable = availablePeriods.some((p) => p.value === currentPeriod)

        // If current period is not available, set to first available period
        if (!isCurrentPeriodAvailable) {
          form.setFieldValue('period', availablePeriods[0].value)
        }
      }
    }
  }
)

const { alert, requestId, formBlock, handleSuccessSubmit } = useFormRequestSuccess()
const { trackEvent } = useAnalytics()

const handleServerValidationErrors = <T extends string>(
  error: unknown,
  setFieldError: (field: T, message: string | string[] | undefined) => void,
  knownFields: T[]
) => {
  if (typeof error === 'object' && error !== null && '_data' in error) {
    const serverError = error as FormServerErrorResponse
    const errors = serverError._data?.errors
    const message = serverError._data?.message

    if (errors && typeof errors === 'object') {
      for (const [key, messages] of Object.entries(errors)) {
        if (Array.isArray(messages) && messages.length) {
          if (knownFields.includes(key as T)) {
            setFieldError(key as T, messages[0])
          }
        }
      }
    } else if (message) {
      toast({
        title: message,
        variant: 'destructive'
      })
    }
  }

}

const onSubmit = form.handleSubmit(async (values) => {
  const organizationType =
    props.deposit?.organization.type ?? props.deposit?.organization.organization_type
  const requestBody: Record<string, unknown> = {
    leadable_type: 'deposit',
    leadable_id: props.deposit?.id || null,
    organization_type: organizationType,
    organization_id: props.deposit?.organization.id,
    location_id: values.location_id,
    currency_id: values.currency_id,
    last_name: values.last_name,
    first_name: values.first_name,
    middle_name: values.middle_name || '',
    phone: `+996${values.phone}`,
    email: values.email,
    amount: values.amount,
    period: values.period,
    fingerprint_id: useFingerprintStore().visitorId
  }

  if (isInnRequired.value && values.inn) {
    requestBody.inn = values.inn
  }

  if (customConsent.value) {
    requestBody.consent_version = customConsent.value.version
  }

  const { data, error } = await useApiPost<IResponseData<{ id: string }>>('/leads', requestBody)
  if (error) {
    const errorFields = [
      'last_name',
      'first_name',
      'middle_name',
      'phone',
      'email',
      'location_id',
      'currency_id',
      'amount',
      'period',
      'agreement'
    ]

    if (isInnRequired.value) errorFields.push('inn')

    handleServerValidationErrors(error, form.setFieldError, errorFields)
    trackEvent('lead_form_error', { lead_type: 'deposit' })
  } else {
    trackEvent('generate_lead', {
      lead_type: 'deposit',
      product_id: props.deposit?.id,
      organization_id: props.deposit?.organization.id,
      organization_type: organizationType
    })
    await handleSuccessSubmit(data?.data.id ?? '')
  }
})
</script>

<template>
  <ContentBlock
    :id="disableAnchor ? undefined : ANCHOR_IDS.FEATURE_DEPOSIT_FORM_REQUEST"
    ref="formBlock"
    :heading-text="props.headingText"
    :heading-tag="props.headingTag"
    :variant="props.variant"
    :show-link-icon="!disableAnchor"
    class="FormScrollOffset"
  >
    <Alert v-if="alert" variant="green">
      <AlertCircle class="h-4 w-4" />
      <AlertTitle>{{ $t('form.credit_request.alert.title') }}</AlertTitle>
      <AlertDescription>
        <div>
          {{ $t('form.deposit_request.alert.description') }}
        </div>
        <div>{{ $t('form.deposit_request.alert.request_id', { id: requestId }) }}</div>
      </AlertDescription>
    </Alert>
    <div v-else>
      <p>
        {{ $t('form.deposit_request.intro') }}
      </p>
      <form
        v-webmcp-tool="{
          name: 'apply_for_deposit',
          description:
            'Fill in an application to open this deposit. The application with the applicant\'s name and contacts is sent to the bank, so the user checks it and submits it.'
        }"
        class="mt-5 flex flex-col gap-5"
        @submit="onSubmit"
      >
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField v-slot="{ componentField, errors }" name="last_name">
            <FormItem>
              <FormLabel>{{ $t('common.last_name') }}*</FormLabel>
              <FormControl>
                <Input type="text" :placeholder="$t('common.last_name')" v-bind="componentField" />
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>
          <FormField v-slot="{ componentField, errors }" name="first_name">
            <FormItem>
              <FormLabel>{{ $t('common.first_name') }}*</FormLabel>
              <FormControl>
                <Input v-bind="componentField" :placeholder="$t('common.first_name')" />
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>
          <FormField v-slot="{ componentField }" name="middle_name">
            <FormItem>
              <FormLabel>{{ $t('common.middle_name') }}</FormLabel>
              <FormControl>
                <Input v-bind="componentField" :placeholder="$t('common.middle_name')" />
              </FormControl>
            </FormItem>
          </FormField>

          <FormField v-if="isInnRequired" v-slot="{ componentField, errors }" name="inn">
            <FormItem>
              <FormLabel>{{ $t('common.inn') }}*</FormLabel>
              <FormControl>
                <WidgetInnInput v-bind="componentField" :errors="errors.length" />
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>

          <FormField v-slot="{ componentField, errors }" name="phone">
            <FormItem>
              <FormLabel>{{ $t('common.phone') }}*</FormLabel>
              <FormControl>
                <WidgetPhoneInput v-bind="componentField" :errors="errors.length" />
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>

          <FormField v-slot="{ componentField, errors }" name="email">
            <FormItem>
              <FormLabel>{{ $t('common.email') }}</FormLabel>
              <FormControl>
                <Input type="email" v-bind="componentField" :placeholder="$t('common.email')" />
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>

          <ClientOnly>
            <FormField v-slot="{ componentField, errors }" name="location_id">
              <FormItem>
                <FormLabel>{{ $t('common.location') }}*</FormLabel>
                <Select v-bind="componentField">
                  <SelectTrigger
                    :aria-label="$t('common.select_city')"
                    :aria-invalid="errors.length > 0"
                  >
                    <SelectValue :placeholder="$t('common.select_city')" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem v-for="city in cities" :key="city.id" :value="city.id">{{
                      city.title
                    }}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage v-if="errors.length" :message="errors[0]" />
              </FormItem>
            </FormField>
          </ClientOnly>

          <FormField v-slot="{ componentField, errors }" name="period">
            <FormItem>
              <FormLabel>{{ $t('form.deposit_request.fields.period') }}</FormLabel>
              <Select v-bind="componentField">
                <SelectTrigger
                  :aria-label="$t('form.deposit_request.aria_labels.period')"
                  :aria-invalid="errors.length > 0"
                >
                  <SelectValue :placeholder="$t('form.credit_request.placeholders.period')" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="period in periods" :key="period.value" :value="period.value">{{
                    period.title
                  }}</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>

          <FormField v-slot="{ componentField, errors }" name="amount">
            <FormItem>
              <FormLabel>{{ $t('form.deposit_request.fields.amount_currency') }}</FormLabel>
              <FormControl>
                <div
                  :class="[
                    'flex h-[2.25rem] items-center gap-0 overflow-hidden rounded-lg border ring-offset-2',
                    errors.length
                      ? 'border-red-200 bg-red-50 focus-within:ring-2 focus-within:ring-red-500'
                      : 'border-gray-300 focus-within:ring-2 focus-within:ring-blue-600'
                  ]"
                >
                  <Input
                    type="number"
                    class="flex-1 rounded-none border-0 bg-transparent focus:ring-0 focus-visible:ring-0"
                    min="1"
                    v-bind="componentField"
                    :placeholder="$t('form.credit_request.placeholders.amount')"
                  />

                  <FormField v-slot="{ componentField: currencyField }" name="currency_id">
                    <Select v-bind="currencyField" :disabled="currencies.length === 1">
                      <SelectTrigger
                        class="w-[100px] rounded-none border-0 border-l bg-transparent focus:ring-0 focus-visible:ring-0"
                        :aria-label="$t('common.currency')"
                      >
                        <SelectValue :placeholder="$t('common.choose_currency')" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          v-for="currency in currencies"
                          :key="currency.id"
                          :value="currency.id"
                        >
                          <div class="flex items-center gap-2">
                            <CurrencyIcon :code="currency.code" size="sm" country />
                            {{ currency.code }}
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormField>
                </div>
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>
        </div>

        <FormField v-slot="{ value, errorMessage, handleChange }" type="checkbox" name="agreement">
          <FormItem>
            <FormControl>
              <div class="flex items-center space-x-2">
                <Checkbox id="terms" :model-value="value" @update:model-value="handleChange" />
                <Label for="terms">
                  <FeatureCustomConsent v-if="customConsent" :consent="customConsent" />
                  <template v-else>
                    {{ $t('form.contact.agreement_prefix') }}
                    <NuxtLinkLocale to="/privacy-policy" class="text-blue-600 underline">
                      {{ $t('form.contact.agreement_link') }}
                    </NuxtLinkLocale>
                  </template>
                </Label>
              </div>
            </FormControl>
            <FormMessage v-if="errorMessage" :message="errorMessage" />
          </FormItem>
        </FormField>

        <div class="flex flex-col items-start gap-4 md:flex-row md:justify-between">
          <Button type="submit" class="mt-4 md:mt-0">{{ $t('form.contact.submit') }}</Button>
        </div>
      </form>
    </div>
  </ContentBlock>
</template>
